/**
 * **A port of `legacy/frontend/src/screens/PastRoster.tsx`, whole but for its `:3-9`.** F-014 T6: the
 * roster of one closed gathering, with the row sheets the edit window allows. `PastGathering` (T9) is
 * its caller.
 *
 * Their `:3-9` are one import that mixes `ApiError` and `api` with the `AttendanceStatus`, `LiveRow`
 * and `SessionAttendance` types. The three are server shapes, so here they come from
 * `@contracts/index` (ADR-0011) and the two values from `../api` — `format.ts`'s answer, the same
 * mixed import split in two. Those two lines are the only ones that differ. Their `:1-2` and their
 * `:10` to the end are theirs byte for byte, and `scripts/ported-partial.txt` holds both regions.
 *
 * Their `:69` stores «Не удалось загрузить состав» and `:181-182` never draws it — `FND-583`, `KEEP`,
 * carried as written.
 */
import { useCallback, useEffect, useState } from 'react';

import type { AttendanceStatus, LiveRow, SessionAttendance } from '@contracts/index';
import { ApiError, api } from '../api';
import { arrivalInputValue, arrivalIso, arrivalProblem, plural } from '../format';
import AttendanceRow from './AttendanceRow';
import { Failed, Settling, Skeleton } from './States';

/**
 * The roster of one closed gathering, with the toggles the edit window allows.
 *
 * It was the second half of a screen called «Что уже прошло», which held both
 * the list of closed gatherings and the roster of whichever one was open. The
 * list is the tail of the schedule since 2026-08-22 and the roster is here,
 * under the summary of the same evening — which is the seam the audit named:
 * the regent closed a gathering on one tab and checked its roster on another,
 * and a variable in `App` carried the session id between them.
 *
 * Editing the past works end to end on the backend and had no way in at all
 * before this screen existed: the regent could correct only the gathering that
 * was running.
 */
export default function PastRoster({
  sessionId,
  start,
  onEdited,
}: {
  sessionId: string;
  /**
   * When the gathering actually began, which a typed arrival is measured
   * against. It is not on the roster's own answer, and the arithmetic about
   * time stays where it is decided — see `arrivalIso` in `format.ts`.
   */
  start: string;
  /** A correction changes the summary above, which is the parent's to redraw. */
  onEdited?: (attendance: SessionAttendance) => void;
}) {
  const [composition, setComposition] = useState<SessionAttendance | null>(null);
  // The expanded row. Kept apart from the composition so that reloading after an
  // edit does not close the toggles under the regent's finger.
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The refusal and where it belongs: `screen` for the roster itself, a member
   * id for anything done to one row. It was one string at the foot of the
   * screen, so a mark corrected inside the third closed rehearsal answered
   * below the last one — the defect the schedule had, reported on 2026-08-21.
   */
  const [failure, setFailure] = useState<{ at: string | number; text: string } | null>(null);
  /** Reasons being typed in, by member. Absent means "not touched since the load". */
  const [excuseDrafts, setExcuseDrafts] = useState<Record<number, string>>({});
  /** Arrival times being typed in, by member. Same rule: absent means untouched. */
  const [arrivalDrafts, setArrivalDrafts] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    try {
      const loaded = await api.sessionAttendance(sessionId);
      setComposition(loaded);
      onEdited?.(loaded);
      setFailure(null);
    } catch (error) {
      setFailure({
        at: 'screen',
        text: error instanceof ApiError ? error.message : 'Не удалось загрузить состав',
      });
    }
    // `onEdited` is the parent's redraw and changes on every render of it;
    // depending on it would reload the roster in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Edit, then reload the roster: the screen has to show the new status. */
  async function edit(
    action: () => Promise<unknown>,
    fallback: string,
    /** Whose row the refusal belongs to — every caller here is about one. */
    at: string | number,
  ): Promise<boolean> {
    setBusy(true);
    setFailure(null);
    try {
      await action();
      await load();
      return true;
    } catch (error) {
      setFailure({ at, text: error instanceof ApiError ? error.message : fallback });
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(row: LiveRow, status: AttendanceStatus): Promise<void> {
    await edit(
      () => api.markManually({ sessionId, memberId: row.member_id, status }),
      'Не удалось изменить отметку',
      row.member_id,
    );
    // Any other status rewrote the row's time on the server, so a time typed
    // into the old one and never saved is no longer about anything.
    if (status !== 'late') dropArrivalDraft(row.member_id);
  }

  function dropArrivalDraft(memberId: number): void {
    setArrivalDrafts((current) => {
      const next = { ...current };
      delete next[memberId];
      return next;
    });
  }

  function arrivalOf(row: LiveRow): string {
    return arrivalDrafts[row.member_id] ?? arrivalInputValue(row);
  }

  /** Keeps the status and only adds the minutes; an empty field saves nothing. */
  async function saveArrival(row: LiveRow): Promise<void> {
    const problem = arrivalProblem(start, arrivalOf(row));
    if (problem !== null) {
      setFailure({ at: row.member_id, text: problem });
      return;
    }
    const markedAt = arrivalIso(start, arrivalOf(row));
    if (markedAt === null) return;
    const saved = await edit(
      () => api.markManually({ sessionId, memberId: row.member_id, status: 'late', markedAt }),
      'Не удалось сохранить время',
      row.member_id,
    );
    if (!saved) return;
    dropArrivalDraft(row.member_id);
  }

  async function toggleExcuse(row: LiveRow): Promise<void> {
    await edit(
      // The reason goes with the flag it explains, so taking the flag off drops
      // it and putting the flag back starts from an empty field.
      () => api.setExcused(sessionId, row.member_id, !row.is_excused),
      'Не удалось изменить уважительность',
      row.member_id,
    );
    setExcuseDrafts((current) => {
      const next = { ...current };
      delete next[row.member_id];
      return next;
    });
  }

  function excuseNoteOf(row: LiveRow): string {
    return excuseDrafts[row.member_id] ?? row.excuse_note ?? '';
  }

  /** Keeps the flag on and only writes the reason; the draft goes on failure. */
  async function saveExcuseNote(row: LiveRow): Promise<void> {
    const typed = excuseNoteOf(row).trim();
    const saved = await edit(
      () => api.setExcused(sessionId, row.member_id, true, typed === '' ? null : typed),
      'Не удалось сохранить причину',
      row.member_id,
    );
    if (!saved) return;
    setExcuseDrafts((current) => {
      const next = { ...current };
      delete next[row.member_id];
      return next;
    });
  }

  // Nothing at all yet, or why. Once the roster is on screen a failed edit is
  // drawn inside the row that earned it and this stays out of the way.
  if (composition === null) {
    return failure?.at === 'screen' ? (
      <Failed subject="history" onRetry={() => void load()} />
    ) : (
      <Skeleton />
    );
  }

  return (
    <Settling>
    <div className="block">
      <span className="label">
        Состав · <span className="section-count">{composition.member_count}</span>
      </span>

      {composition.can_edit ? (
        <p className="hint">Отметку можно поправить в течение 14 дней после закрытия.</p>
      ) : (
        <p className="hint">
          Прошло больше 14 дней после закрытия — это уже нельзя изменить. Окно правок конечно
          нарочно: иначе прошлое никогда не становится историей.
        </p>
      )}

      <ul className="live">
        {composition.rows.map((row) => (
          <AttendanceRow
            key={row.member_id}
            row={row}
            expanded={editing === row.member_id}
            busy={busy}
            editable={composition.can_edit}
            onToggle={() => setEditing(editing === row.member_id ? null : row.member_id)}
            onSetStatus={(status) => void setStatus(row, status)}
            onToggleExcuse={() => void toggleExcuse(row)}
            arrivalTime={{
              value: arrivalOf(row),
              start,
              // An empty field is not a way to say "unknown after all": the
              // server reads no time as "leave what is there", so there would
              // be nothing to save.
              dirty: arrivalOf(row) !== '' && arrivalOf(row) !== arrivalInputValue(row),
              onChange: (value) =>
                setArrivalDrafts((current) => ({ ...current, [row.member_id]: value })),
              onSave: () => void saveArrival(row),
            }}
            excuseNote={{
              value: excuseNoteOf(row),
              dirty: excuseNoteOf(row).trim() !== (row.excuse_note ?? ''),
              onChange: (value) =>
                setExcuseDrafts((current) => ({ ...current, [row.member_id]: value })),
              onSave: () => void saveExcuseNote(row),
            }}
            /* Inside the sheet the correction was made in, over the button that
               was refused. It used to stand under the list, which on a roster
               of forty names is nowhere near the tap. */
            failure={failure !== null && failure.at === row.member_id ? failure.text : null}
          />
        ))}
      </ul>

      <p className="hint">
        Пришли {composition.present_count} из {composition.member_count}{' '}
        {plural(composition.member_count, ['человека', 'человек', 'человек'])}.
      </p>
    </div>
    </Settling>
  );
}
